import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ethers } from 'ethers';

import Alert from './Alert';
import { removeLiquidity, loadBalances } from '../store/interactions';
import { getNetworkConfig } from '../utils/network';
import { formatAmount, shortenAddress, friendlyError } from '../utils/format';
import AMM_ABI from '../abis/AMM.json';

const Withdraw = () => {
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');
  const [targetAmm, setTargetAmm] = useState('amm1');
  const [showAlert, setShowAlert] = useState(false);
  const [userMessage, setUserMessage] = useState('');

  const provider = useSelector((state) => state.provider.connection);
  const account = useSelector((state) => state.provider.account);
  const chainId = useSelector((state) => state.provider.chainId);
  const amm1Balances = useSelector((state) => state.tokens.amm1Balances);
  const amm2Balances = useSelector((state) => state.tokens.amm2Balances);
  const isWithdrawing = useSelector((state) => state.amm.withdrawing.isWithdrawing);
  const isSuccess = useSelector((state) => state.amm.withdrawing.isSuccess);
  const transactionHash = useSelector((state) => state.amm.withdrawing.transactionHash);
  const dispatch = useDispatch();

  const networkConfig = useMemo(() => getNetworkConfig(chainId), [chainId]);

  const poolBalances = targetAmm === 'amm2' ? amm2Balances : amm1Balances;
  const selectedAddress =
    networkConfig &&
    (targetAmm === 'amm2' ? networkConfig.amm2.ammAddress : networkConfig.amm1.ammAddress);

  const withdrawHandler = async (e) => {
    e.preventDefault();
    setShowAlert(false);
    setUserMessage('');

    if (!provider || !account || !networkConfig) {
      setUserMessage('Connect wallet on a supported network first.');
      return;
    }

    try {
      const qtyA = ethers.utils.parseUnits(amountA || '0', 'ether');
      const qtyB = ethers.utils.parseUnits(amountB || '0', 'ether');
      if (qtyA.lte(0) || qtyB.lte(0)) {
        setUserMessage('Enter positive amounts for both tokens to withdraw.');
        return;
      }

      const ammAddress =
        targetAmm === 'amm2' ? networkConfig.amm2.ammAddress : networkConfig.amm1.ammAddress;
      const amm = new ethers.Contract(ammAddress, AMM_ABI, provider);

      await removeLiquidity(
        provider,
        amm,
        { address: networkConfig.amm1.dappTokenAddress },
        { address: networkConfig.amm1.usdTokenAddress },
        qtyA,
        qtyB,
        dispatch
      );

      await loadBalances(provider, networkConfig, dispatch);
      setShowAlert(true);
      setAmountA('');
      setAmountB('');
    } catch (error) {
      console.error(error);
      setUserMessage(friendlyError(error, 'Withdraw failed.'));
      setShowAlert(true);
    }
  };

  const ctaLabel = isWithdrawing ? 'Withdrawing…' : 'Remove Liquidity';

  return (
    <div style={{ maxWidth: 'var(--content-max)', margin: '0 auto' }}>
      <section className="card card--elevated" aria-labelledby="withdraw-title">
        <p className="card__eyebrow">Liquidity</p>
        <h1 id="withdraw-title" className="card__title">
          Remove Liquidity
        </h1>
        <p className="card__subtitle">
          Withdraw demo tokens from a selected AMM. Simplified pool model — not ERC-20 LP shares.
        </p>

        {account && networkConfig ? (
          <form onSubmit={withdrawHandler}>
            <div className="field">
              <label className="field__label" htmlFor="withdraw-amm">
                Selected AMM
              </label>
              <select
                id="withdraw-amm"
                className="field__select"
                value={targetAmm}
                onChange={(e) => setTargetAmm(e.target.value)}
              >
                <option value="amm1">AMM 1 · {shortenAddress(networkConfig.amm1.ammAddress)}</option>
                <option value="amm2">AMM 2 · {shortenAddress(networkConfig.amm2.ammAddress)}</option>
              </select>
            </div>

            {poolBalances && (
              <p className="helper-text" style={{ marginTop: 0, marginBottom: 16 }}>
                Available pool · DAPP{' '}
                <span title={poolBalances.dappTokenBalance}>
                  {formatAmount(poolBalances.dappTokenBalance, 4)}
                </span>
                {' · '}
                USD{' '}
                <span title={poolBalances.usdTokenBalance}>
                  {formatAmount(poolBalances.usdTokenBalance, 4)}
                </span>
                {selectedAddress && (
                  <>
                    {' · '}
                    <span title={selectedAddress}>{shortenAddress(selectedAddress)}</span>
                  </>
                )}
              </p>
            )}

            <div className="token-panel" style={{ marginBottom: 12 }}>
              <div className="token-panel__top">
                <span className="token-panel__label">DAPP amount</span>
              </div>
              <div className="token-panel__row">
                <input
                  className="token-panel__amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder="0.0"
                  aria-label="DAPP withdraw amount"
                  value={amountA}
                  onChange={(e) => setAmountA(e.target.value)}
                />
                <span className="token-select" style={{ pointerEvents: 'none' }}>
                  DAPP
                </span>
              </div>
            </div>

            <div className="token-panel">
              <div className="token-panel__top">
                <span className="token-panel__label">USD amount</span>
              </div>
              <div className="token-panel__row">
                <input
                  className="token-panel__amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  placeholder="0.0"
                  aria-label="USD withdraw amount"
                  value={amountB}
                  onChange={(e) => setAmountB(e.target.value)}
                />
                <span className="token-select" style={{ pointerEvents: 'none' }}>
                  USD
                </span>
              </div>
            </div>

            {userMessage && !showAlert && (
              <p className="helper-text" role="status" style={{ color: 'var(--warning)' }}>
                {userMessage}
              </p>
            )}

            <div className="btn-row">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isWithdrawing}
                aria-busy={isWithdrawing}
              >
                {isWithdrawing && <span className="spinner" aria-hidden="true" />}
                {ctaLabel}
              </button>
            </div>
          </form>
        ) : (
          <p className="helper-text">Connect wallet on Sepolia or Localhost to withdraw.</p>
        )}
      </section>

      {isWithdrawing ? (
        <Alert
          message="Withdrawing…"
          transactionHash={null}
          variant="info"
          setShowAlert={setShowAlert}
        />
      ) : isSuccess && showAlert ? (
        <Alert
          message="Transaction Confirmed"
          transactionHash={transactionHash}
          variant="success"
          setShowAlert={setShowAlert}
        />
      ) : !isSuccess && showAlert ? (
        <Alert
          message={userMessage || 'Transaction Failed'}
          transactionHash={null}
          variant="danger"
          setShowAlert={setShowAlert}
        />
      ) : null}
    </div>
  );
};

export default Withdraw;
